import { useState, useEffect } from "react";
import {
  Table,
  Card,
  Tag,
  Typography,
  Space,
  Spin,
  Button,
  Empty,
} from "antd";
import { useParams } from "react-router-dom";
import { segmentsApiService } from "../../../api/segments";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import "./call-insights.scss";
import CallsGraphs from "./CallsGraphs";
import { useBulkDownload } from "../../../hooks";

const { Title, Text } = Typography;

export const CallsInsights = () => {
  const [segmentsData, setSegmentsData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { callId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const fileName = queryParams.get("fileName");

  const [segmentsAnalytics, setSegmentsAnalytics] = useState<any>(null);

  const { handleBulkDownload, isBulkDownloading } = useBulkDownload()

  useEffect(() => {
    const fetchSegmentsData = async () => {
      try {
        setLoading(true);

        if (typeof callId === "string") {
          if (fileName) {
            const response = await segmentsApiService.getSegmentsData(
              callId,
              fileName
            );
            console.log(response.segments);
            
            const data = response.segments;
            setSegmentsData(data);
          } else {
            console.error("fileName is null");
          }
        }
      } catch (error) {
        console.error("Error fetching segments data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (callId) {
      fetchSegmentsData();
    }
  }, [callId]);

  const getSentimentColor = (sentiment: number) => {
    const colors = {
      1: "green",
      0: "orange",
      "-1": "red",
    };
    return colors[sentiment as keyof typeof colors] || "default";
  };

  const getEmotionColor = (emotion: number) => {
    const colors = {
      1: "blue",
      0: "purple",
      "-1": "red",
    };
    return colors[emotion as keyof typeof colors] || "default";
  };

  const columns: any = [
    {
      title: "Segment ID",
      dataIndex: "segment_id",
      className: "w-20",
      key: "segment_id",
    },
    {
      title: "Topic",
      dataIndex: "topic_classification",
      key: "topic",
      align: "center",
      className: "w-20",
      render: (topic: string) => {
        const [mainCategory] = topic.split("-").map((s) => s.trim());
        return <Tag color="geekblue">{mainCategory}</Tag>;
      },
    },
    {
      title: "Sub Topic",
      dataIndex: "topic_classification",
      key: "sub_topic",
      align: "center",
      className: "w-20",
      render: (topic: string) => {
        const parts = topic.split("-").map((s) => s.trim());
        const subCategory = parts[1] ? parts[1] : "Others";
        return <Tag color="blue">{subCategory}</Tag>;
      },
    },
    {
      title: "Affectation",
      dataIndex: "final_affectation_class",
      key: "final_affectation_class",
      align: "center",
      className: "w-20",
      render: (text: string) => {
        const affectationColors: { [key: string]: string } = {
          Activated: "green",
          Neutral: "orange",
          Deactivated: "red",
        };

        return <Tag color={affectationColors[text] || "default"}>{text}</Tag>;
      },
    },
    {
      title: "Emotion",
      dataIndex: "emotion_classification",
      key: "emotion_classification",
      align: "center",
      className: "w-20",
      render: (emotion: number) => {
        if (![1, 0, -1].includes(emotion)) return null; // Hide invalid values
    
        return (
          <Tag color={getEmotionColor(emotion)}>
            {emotion === 1 ? "Positive" : emotion === 0 ? "Neutral" : "Negative"}
          </Tag>
        );
      },
    },  
    {
      title: "Sentiment",
      dataIndex: "sentiment_classification",
      key: "sentiment_classification",
      align: "center",
      className: "w-20",
      render: (sentiment: any) => (
        <Tag color={getSentimentColor(sentiment)}>
          {sentiment === 1
            ? "Positive"
            : sentiment === 0
            ? "Neutral"
            : "Negative"}
        </Tag>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      className: "w-20",
      align: "center",
      render: (duration: string) => (
        <p style={{ color: "red", fontSize: "12px" }}>
          {parseInt(duration) / 1000 + " s"}
        </p>
      ),
    },
    {
      title: "Action",
      key: "expand",
      align: "center",
      className: "w-20",
      render: (_: any, _record: any, expanded: boolean) => (
        <div className="cursor-pointer">
          <DownOutlined />
        </div>
      ),
    },
  ];

  const formatSegments = (text: any) => {
    const lines = text.split("\n");
    return lines.map((line: any, index: any) => {
      const match = line.match(/^(person\d+):\s*(.*)/);
      if (match) {
        return (
          <div key={index} className="mb-2">
            <span className="font-semibold text-blue-600">{match[1]}: </span>
            <span>{match[2]}</span>
          </div>
        );
      }
      return <div key={index}>{line}</div>;
    });
  };

  const formatReasoning = (text: string) => {
    const lines = text.split("\n");
    return lines
      .filter((line) => line.match(/^\d+\./))
      .map((line, index) => {
        const [number, ...content] = line.split(".");
        return (
          <div key={index} className="mb-2 ml-4">
            <span className="font-semibold">{number}. </span>
            <span>{content.join(".").trim()}</span>
          </div>
        );
      });
  };

  const formatTopicClassificationReasoning = (text: string) => {
    return text
      .split(". ")
      .filter((line) => line.trim() !== "")
      .map((line, index) => (
        <div key={index} className="mb-2">
          <span>
            {index + 1}. {line.trim()}.
          </span>
        </div>
      ));
  };

  const getChartData = () => {
    if (!segmentsAnalytics) return null;

    // Transform topic data
    const topicData: any = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
            "#FF9F40",
            "#C9CBCF",
          ],
        },
      ],
    };

    // Extract topics and percentages
    Object.entries(segmentsAnalytics.topicPercentages[0]).forEach(
      ([topic, percentage]) => {
        topicData.labels.push(topic);
        // Remove ' %' and convert to number
        topicData.datasets[0].data.push(
          parseInt((percentage as string).replace(" %", ""))
        );
      }
    );

    // Transform emotion data
    const emotionData: {
      labels: string[];
      datasets: { data: number[]; backgroundColor: string[] }[];
    } = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
        },
      ],
    };

    const emotions = [
      { key: "positive", label: "Positive", color: "#4CAF50" },
      { key: "negative", label: "Negative", color: "#F44336" },
      { key: "neutral", label: "Neutral", color: "#FFC107" },
    ];

    emotions.forEach((emotion: any) => {
      const value = parseInt(
        segmentsAnalytics.emotionPercentages[emotion.key].replace(" %", ""),
        10
      );
      if (value > 0) {
        emotionData.labels.push(emotion.label);
        emotionData.datasets[0].data.push(value);
        emotionData.datasets[0].backgroundColor.push(emotion.color);
      }
    });

    return { topicData, emotionData };
  };

  const getSegmentsAnalytics = async () => {
    try {
      if (typeof callId === "string") {
        if (fileName) {
          const result = await segmentsApiService.getSegmentsAnalytics(
            callId,
            fileName
          );
          setSegmentsAnalytics(result);
        } else {
          console.error("fileName is null");
        }
      }
    } catch (error) {
      console.error("Error fetching segments analytics:", error);
    }
  };

  useEffect(() => {
    getSegmentsAnalytics();
  }, []);

  const chartData = getChartData();

  if (!chartData) return <Spin/>;

  const expandedRowRender = (record: any) => (
    <div>
      <Card size="small" className="mx-4 my-2 bg-gray-50">
        <Space direction="vertical" className="w-full">
          <div>
            <Text strong>Segment:</Text>
            <div className="ml-2 mt-1">{formatSegments(record.segment)}</div>
          </div>
          <div className="mt-2">
            <Text strong>Reasoning for Determining Affectation:</Text>
            <div className="ml-2 mt-1 ">
              {formatSegments(record.step_by_step_reasoning)}
            </div>
          </div>
          <div className="mt-2">
            <Text strong>Reasoning for topic classifcation:</Text>
            <div className="ml-2 mt-1 ">
              {formatTopicClassificationReasoning(
                record.step_by_step_reasoning_for_topic_classification
              )}
            </div>
          </div>
        </Space>
      </Card>
    </div>
  );

  const handleExpand = (expanded: boolean, record: any) => {
    setExpandedRowId(expanded ? record.id : null);
  };

  return (
     <Spin spinning={isBulkDownloading || loading} tip="Processing...">
      <div className="w-full px-4">
        <div className="flex jcsb">
          <Title level={4}>
            <Text strong style={{ color: "#673AB7" }}>{fileName}</Text> / <Text>{callId}</Text>
          </Title>
          <Button 
            icon={<DownloadOutlined />} 
            className="outline" 
            onClick={() => {
              if (!callId || !fileName) {
                console.error("callId or fileName is missing.");
                return;
              }
              handleBulkDownload([{ callId: callId ?? "", fileName: fileName ?? "" }]);
            }}
          >
            Download Output
          </Button>
        </div>

        <CallsGraphs chartData={chartData} />

        <div className="mt-4">
          <Table
            columns={columns}
            dataSource={segmentsData}
            rowKey="id"
            className="w-full"
            expandable={{
              expandedRowRender,
              expandRowByClick: true,
              expandedRowKeys: expandedRowId ? [expandedRowId] : [],
              onExpand: handleExpand,
              expandIcon: ({ expanded, onExpand, record }) => null,
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} segments`,
            }}
            locale={{
              emptyText: <Empty description="No data available" />,
            }}
          />
        </div>
      </div>
    </Spin>
  );
};

export default CallsInsights;